/**
 * Where are you working?
 *
 * D43 is the reason this screen exists and the reason nothing is preselected.
 * Pre-filling from the rota would make the common case one tap, but it risks
 * silently accepting a stale default after someone has moved — which corrupts
 * the staffing picture HQ depends on. Deliberate friction, and one of the few
 * places in this design where the slower option is the right one.
 *
 * D34: what is chosen here is recorded as the person's actual location.
 */

import { useEffect, useState } from 'react';

import type { Checkpoint } from '@cairn/domain';

import { listCheckpoints } from './data/checkpoints.js';
import { setLocation, type Membership } from './data/members.js';

interface Props {
  readonly membership: Membership;
  readonly uid: string;
}

export function ChooseLocation({ membership, uid }: Props): React.JSX.Element {
  const [checkpoints, setCheckpoints] = useState<readonly Checkpoint[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listCheckpoints(membership.eventId).then((list) => {
      if (!cancelled) setCheckpoints(list);
    });
    return () => {
      cancelled = true;
    };
  }, [membership.eventId]);

  async function choose(checkpointId: string): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await setLocation(membership.eventId, uid, checkpointId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      setSaving(false);
    }
  }

  return (
    <section>
      <h1>Where are you working?</h1>
      <p>Choose the checkpoint you are at now. Nothing is preselected on purpose.</p>

      {checkpoints === null ? (
        <p>Loading checkpoints…</p>
      ) : (
        <ul>
          {checkpoints.map((checkpoint) => (
            <li key={checkpoint.id}>
              <button type="button" disabled={saving} onClick={() => void choose(checkpoint.id)}>
                {checkpoint.number}. {checkpoint.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      {error === null ? null : <p role="alert">{error}</p>}
    </section>
  );
}
